// src/components/TellerDashboard.js
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { deleteField, doc, setDoc } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { db } from '../firebase';
import Header from './Header';
import Footer from './Footer';
import TellerProPanel from './TellerProPanel';
import { AuthContext } from '../context/AuthContext';
import { ADMIN_EMAIL } from '../services/auth';
import { useI18n } from '../i18n/I18nContext';
import { pickPageCopy, tellerDashboardCopy } from '../i18n/pageCopy';
import { getLocalizedError } from '../i18n/errorCopy';
import { localizeReaderText } from '../i18n/readerTerminology';
import { translateSessionMode, translateSessionName, translateSessionStatus } from '../i18n/sessionCopy';
import fallbackTellerImage from '../assets/images/tarot-empress.jpg';
import { isValidIban, normalizeIban } from '../utils/paymentQr';
import {
  approveCancelSession,
  createTellerAvailability,
  confirmSessionPayment,
  declineCancelSession,
  formatDateTime,
  getProfile,
  isFutureSession,
  markSessionCompleted,
  parseSessionStart,
  SESSION_NAME_OPTIONS,
  subscribeTellerAvailability,
  subscribeTellerSessions,
  toNumber,
} from '../services/marketplace';
import {
  formatNotificationTime,
  markNotificationRead,
  subscribeNotifications,
} from '../services/notifications';
import '../style/Marketplace.css';
import '../style/TellerDashboard.css';

const initialSlotForm = {
  sessionName: 'Tarot reading',
  description: '',
  date: '',
  time: '10:00',
  duration: '60',
  price: '30',
  currency: 'EUR',
  sessionType: 'video',
};

const WHOLE_HOUR_OPTIONS = Array.from(
  { length: 24 },
  (_, hour) => `${String(hour).padStart(2, '0')}:00`,
);

const localDateKey = (value) => {
  const date = value instanceof Date ? value : parseSessionStart(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

const statusLabel = (session, t) => {
  if (session.status === 'cancelled') return t('workflow.dashboard.cancelled');
  if (session.cancelStatus === 'pending' || session.cancelRequested) return t('workflow.dashboard.cancelPending');
  if (session.status === 'completed') return t('workflow.dashboard.completed');
  if (session.status === 'paid' || session.status === 'confirmed') return t('workflow.dashboard.confirmed');
  if (session.status === 'pending') return t('workflow.dashboard.pending');
  return session.status || t('workflow.dashboard.booked');
};

const formatPaymentAmount = (amount, currency = 'EUR') => {
  const value = toNumber(amount).toFixed(2);
  return String(currency).toUpperCase() === 'CZK' ? `${value} Kč` : `€${value}`;
};

const TellerDashboard = () => {
  const { user, userType } = useContext(AuthContext);
  const { language, locale, t } = useI18n();
  const copy = pickPageCopy(language, tellerDashboardCopy);
  const isAdmin = userType === 'admin'
    || String(user?.email || '').trim().toLowerCase() === ADMIN_EMAIL;
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [slots, setSlots] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [form, setForm] = useState(initialSlotForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [iban, setIban] = useState('');
  const [savingIban, setSavingIban] = useState(false);
  const [slotError, setSlotError] = useState('');
  const [slotMessage, setSlotMessage] = useState('');
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(() => new Date());
  const [notifications, setNotifications] = useState([]);
  const profileApproved = profile?.approved === true
    && (!profile.approvalStatus || profile.approvalStatus === 'approved')
    && profile?.banned !== true;

  const orderedSessions = useMemo(() => [...sessions].sort((a, b) => {
    const aStart = parseSessionStart(a)?.getTime() || 0;
    const bStart = parseSessionStart(b)?.getTime() || 0;
    return bStart - aStart;
  }), [sessions]);

  useEffect(() => {
    if (isAdmin) {
      navigate('/admin-login', { replace: true });
      return;
    }
    if (!user) {
      navigate('/login');
      return;
    }
    getProfile(user.uid, { includeTellerProfile: true }).then((data) => {
      const loadedProfile = data || { email: user.email, role: 'teller' };
      setProfile(loadedProfile);
      setIban(data?.iban || '');
      if (normalizeIban(data?.iban || '').startsWith('CZ')) {
        setForm((current) => ({ ...current, currency: 'CZK' }));
      }
      setLoading(false);
      const approved = loadedProfile.approved === true
        && (!loadedProfile.approvalStatus || loadedProfile.approvalStatus === 'approved')
        && loadedProfile.banned !== true;
      if (!approved) navigate('/onboarding/teller', { replace: true });
    }).catch((err) => {
      setError(getLocalizedError(err, language, copy.actionError));
      setLoading(false);
    });
  }, [copy.actionError, isAdmin, language, navigate, user]);

  useEffect(() => {
    if (!user || !profileApproved) return undefined;
    const unsubSlots = subscribeTellerAvailability(
      user.uid,
      setSlots,
      (err) => setError(getLocalizedError(err, language, copy.actionError)),
      { includeReserved: true, includePast: true },
    );
    const unsubSessions = subscribeTellerSessions(
      user.uid,
      user.email,
      setSessions,
      (err) => setError(getLocalizedError(err, language, copy.actionError)),
    );
    return () => {
      unsubSlots();
      unsubSessions();
    };
  }, [copy.actionError, language, profileApproved, user]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    return subscribeNotifications(
      user.uid,
      'teller',
      setNotifications,
      (err) => setError(getLocalizedError(err, language, copy.notificationsError)),
    );
  }, [copy.notificationsError, language, user]);

  const slotsByDate = useMemo(() => {
    const grouped = new Map();
    slots.forEach((slot) => {
      const key = localDateKey(slot);
      if (!key) return;
      grouped.set(key, [...(grouped.get(key) || []), slot]);
    });
    return grouped;
  }, [slots]);

  const selectedDaySlots = useMemo(
    () => slotsByDate.get(localDateKey(selectedCalendarDate)) || [],
    [selectedCalendarDate, slotsByDate],
  );

  const stats = useMemo(() => {
    const paid = sessions.filter((s) => ['paid', 'confirmed', 'completed'].includes(s.status));
    const completed = sessions.filter((s) => s.status === 'completed');
    const revenue = paid.reduce((sum, session) => sum + toNumber(session.price), 0);
    const openSlots = slots.filter((slot) => slot.status === 'available' && isFutureSession(slot)).length;
    return { revenue, paid: paid.length, completed: completed.length, openSlots };
  }, [sessions, slots]);

  const handleAddSlot = async (e) => {
    e.preventDefault();
    if (!profileApproved) {
      navigate('/onboarding/teller');
      return;
    }
    setSaving(true);
    setSlotError('');
    setSlotMessage('');
    try {
      await createTellerAvailability({ user, profile, form });
      // Keep the teller's selected currency for the next slot instead of reverting to EUR.
      setForm((current) => ({ ...initialSlotForm, currency: current.currency }));
      setSlotMessage(t('workflow.teller.slotCreated'));
    } catch (err) {
      setSlotError(getLocalizedError(err, language, copy.createError));
    } finally {
      setSaving(false);
    }
  };

  const handleAction = async (action, session) => {
    setError('');
    setMessage('');
    try {
      if (action === 'approve-cancel') await approveCancelSession(session, 'teller');
      if (action === 'decline-cancel') await declineCancelSession(session, 'teller');
      if (action === 'complete') await markSessionCompleted(session, 'teller');
      if (action === 'confirm-payment') await confirmSessionPayment(session, user);
      setMessage(action === 'confirm-payment' ? t('workflow.teller.paymentConfirmed') : t('workflow.teller.updated'));
    } catch (err) {
      setError(getLocalizedError(err, language, copy.actionError));
    }
  };

  const handleIbanSave = async (event) => {
    event.preventDefault();
    const normalizedIban = normalizeIban(iban);
    if (!isValidIban(normalizedIban)) {
      setError(copy.ibanInvalid);
      return;
    }
    setSavingIban(true);
    setError('');
    setMessage('');
    const updatedAt = new Date().toISOString();
    const publicProfilePayload = {
      // The payment account stays private; a booking function copies it only
      // into the session that the client has reserved.
      iban: deleteField(),
      bankAccount: deleteField(),
      paymentReady: true,
      userId: user.uid,
      email: user.email,
      role: 'teller',
      type: 'fortuneTeller',
      updatedAt,
    };
    try {
      await Promise.all([
        setDoc(doc(db, 'users', user.uid), { iban: normalizedIban, updatedAt }, { merge: true }),
        setDoc(doc(db, 'fortuneTellers', user.uid), publicProfilePayload, { merge: true }),
      ]);
      setIban(normalizedIban);
      setProfile((current) => ({ ...current, iban: normalizedIban }));
      setMessage(copy.ibanSaved);
    } catch (err) {
      setError(getLocalizedError(err, language, copy.ibanError));
    } finally {
      setSavingIban(false);
    }
  };

  if (loading) {
    return <><Header /><main className="market-page"><div className="empty-card">{t('workflow.teller.loading')}</div></main><Footer /></>;
  }

  return (
    <div>
      <Header />
      <main className="market-page dashboard-page teller-dashboard-page">
        <div className="teller-dashboard-workspace">
          <aside className="teller-dashboard-sidebar" aria-label={t('a11y.tellerDashboardNavigation')}>
            <div className="teller-sidebar-brand">
              <span className="teller-sidebar-kicker">{t('workflow.teller.control')}</span>
              <h2>{t('workflow.teller.workspace')}</h2>
              <p>{t('workflow.teller.manage')}</p>
            </div>

            <nav className="teller-sidebar-nav">
              <a href="#overview"><span>01</span><strong>{t('workflow.teller.overview')}</strong></a>
              <a href="#notifications"><span>02</span><strong>{t('workflow.teller.notifications')}</strong><i>{notifications.filter((item) => !item.read).length}</i></a>
              <a href="#teller-pro"><span>03</span><strong>{t('workflow.teller.pro')}</strong></a>
              <a href="#availability-editor"><span>04</span><strong>{t('workflow.teller.add')}</strong></a>
              <a href="#booked-sessions"><span>05</span><strong>{t('workflow.teller.booked')}</strong><i>{sessions.length}</i></a>
              <a href="#availability-calendar"><span>06</span><strong>{t('workflow.teller.calendar')}</strong><i>{stats.openSlots}</i></a>
              <a href="#payments"><span>07</span><strong>{t('workflow.teller.payments')}</strong></a>
            </nav>

            <div className="teller-sidebar-profile">
              <img src={profile?.profileImage || profile?.photoUrl || fallbackTellerImage} alt="" />
              <div>
                <strong>{profile?.name || user?.email}</strong>
                <span>{profileApproved ? t('workflow.teller.approved') : t('workflow.teller.pendingReview')}</span>
              </div>
            </div>
          </aside>

          <div className="teller-dashboard-main">
        <section className="dashboard-hero teller-dashboard-hero" id="overview">
          <div>
            <p className="eyebrow">{t('workflow.teller.eyebrow')}</p>
            <h1>{t('workflow.teller.title')}</h1>
            <p>{t('workflow.teller.body')}</p>
            <a className="primary-button teller-pro-jump" href="#teller-pro">{copy.managePro}</a>
          </div>
          <div className="stat-grid compact-stats">
            <article><strong>€{stats.revenue.toFixed(2)}</strong><span>{copy.receivedMoney}</span></article>
            <article><strong>{stats.openSlots}</strong><span>{copy.openSlots}</span></article>
            <article><strong>{stats.paid}</strong><span>{copy.bookings}</span></article>
            <article><strong>{stats.completed}</strong><span>{copy.completed}</span></article>
          </div>
        </section>

        {(error || message) && <div className={error ? 'market-alert' : 'market-success'}>{error || message}</div>}

        <section className="dashboard-card dashboard-notifications" id="notifications">
          <div className="section-title-row">
            <div>
              <p className="eyebrow">{t('workflow.teller.inbox')}</p>
              <h2>{t('workflow.teller.bookingNotifications')}</h2>
            </div>
            <Link to="/notifications">{copy.viewAll}</Link>
          </div>
          {notifications.length === 0 ? (
            <div className="empty-card">{t('workflow.teller.noNotifications')}</div>
          ) : (
            <div className="notification-list compact">
              {notifications.slice(0, 4).map((notification) => (
                <Link
                  key={notification.id}
                  to={notification.actionUrl || '/teller-dashboard'}
                  className={`notification-item ${notification.read ? '' : 'unread'}`}
                  onClick={() => {
                    if (!notification.read) {
                      markNotificationRead(notification.id).catch((err) => {
                        setError(getLocalizedError(err, language, copy.notificationsError));
                      });
                    }
                  }}
                >
                  <span className="notification-symbol" aria-hidden="true">€</span>
                  <span className="notification-copy">
                    <strong>{localizeReaderText(notification.title || copy.newBooking, language)}</strong>
                    <span>{localizeReaderText(notification.body || '', language)}</span>
                    <small>{formatNotificationTime(notification)}</small>
                  </span>
                  {!notification.read && <i aria-label={copy.unread} />}
                </Link>
              ))}
            </div>
          )}
        </section>

        <TellerProPanel user={user} initialPlan={profile?.tellerSubscriptionPlan || 'free'} />

        <section className="dashboard-grid">
          <article className="dashboard-card large-card" id="availability-editor">
            <h2>{t('workflow.teller.addTitle')}</h2>
            <form className="market-form" onSubmit={handleAddSlot}>
              <label>
                {t('workflow.teller.sessionName')}
                <select value={form.sessionName} onChange={(e) => setForm({ ...form, sessionName: e.target.value })} required>
                  {SESSION_NAME_OPTIONS.map((name) => <option key={name} value={name}>{t(`sessionNames.${name}`)}</option>)}
                </select>
              </label>
              <label>{t('workflow.teller.description')}<textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
              <div className="form-grid">
                <label>{t('workflow.teller.date')}<input type="date" min={new Date().toISOString().slice(0, 10)} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required /></label>
                <label>
                  {t('workflow.teller.start')}
                  <select value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required>
                    {WHOLE_HOUR_OPTIONS.map((time) => <option key={time} value={time}>{time}</option>)}
                  </select>
                </label>
              </div>
              <div className="form-grid">
                <label>{t('workflow.teller.duration')}<select value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })}><option value="60">60 min</option><option value="120">120 min</option></select></label>
                <label>{t('workflow.teller.price')}<input type="number" min="1" step="1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required /></label>
                <label>{t('workflow.teller.currency')}<select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}><option value="EUR">EUR (€)</option><option value="CZK">CZK (Kč)</option></select></label>
              </div>
              <label>{t('workflow.teller.sessionType')}<select value={form.sessionType} onChange={(e) => setForm({ ...form, sessionType: e.target.value })}><option value="video">{copy.video}</option><option value="chat">{copy.chat}</option><option value="phone">{copy.phone}</option><option value="email">{copy.email}</option></select></label>
              <button type="submit" disabled={saving}>{saving ? t('workflow.teller.creating') : t('workflow.teller.create')}</button>
              {slotError && <p className="form-inline-message error" role="alert">{slotError}</p>}
              {slotMessage && <p className="form-inline-message success" role="status">{slotMessage}</p>}
            </form>
          </article>

          <article className="dashboard-card" id="payments">
            <h2>{t('workflow.teller.profilePayments')}</h2>
            <div className="mini-profile">
              <img src={profile?.profileImage || profile?.photoUrl || fallbackTellerImage} alt={profile?.name || user?.email} />
              <div><strong>{profile?.name || user?.email}</strong><span>{profile?.bio || profile?.description || copy.bioHint}</span></div>
            </div>
            <form className="market-form iban-form" onSubmit={handleIbanSave}>
              <label>{t('workflow.teller.paymentIban')}<input value={iban} onChange={(event) => setIban(event.target.value)} placeholder="SK00 0000 0000 0000 0000 0000" required /></label>
              <p className="form-help">{copy.qrHelp}</p>
              <button type="submit" disabled={savingIban}>{savingIban ? t('workflow.teller.saving') : t('workflow.teller.saveIban')}</button>
            </form>
            <Link className="secondary-button full" to="/find-teller">{copy.preview}</Link>
          </article>
        </section>

        <section className="dashboard-card teller-sessions-card" id="booked-sessions">
          <div className="section-title-row"><h2>{t('workflow.teller.booked')}</h2><span>{sessions.length} {t('workflow.teller.total')}</span></div>
          {sessions.length === 0 ? <div className="empty-card">{t('workflow.teller.noBooked')}</div> : (
            <div className="responsive-table">
              <table>
                <thead><tr><th>{t('workflow.teller.client')}</th><th>{t('workflow.teller.session')}</th><th>{t('workflow.teller.dateTime')}</th><th>{t('workflow.teller.duration')}</th><th>{t('workflow.teller.price')}</th><th>{t('workflow.teller.status')}</th><th>{t('workflow.teller.actions')}</th></tr></thead>
                <tbody>{orderedSessions.map((session) => (
                  <tr key={session.id}>
                    <td data-label={t('workflow.teller.client')}>{session.clientName || session.clientEmail || session.bookedBy || copy.clientFallback}</td>
                    <td data-label={t('workflow.teller.session')}>{translateSessionName(session.sessionName, t, copy.readingFallback)}</td>
                    <td data-label={t('workflow.teller.dateTime')}>{formatDateTime(session)}</td>
                    <td data-label={t('workflow.teller.duration')}>{session.duration || 60} min</td>
                    <td data-label={t('workflow.teller.price')}>{formatPaymentAmount(session.price, session.currency)}</td>
                    <td data-label={t('workflow.teller.status')}><span className="table-status">{statusLabel(session, t)}</span></td>
                    <td data-label={t('workflow.teller.actions')} className="table-actions">
                      {session.status === 'pending' && <button type="button" onClick={() => handleAction('confirm-payment', session)}>{t('workflow.teller.confirmPayment')}</button>}
                      {['paid', 'confirmed', 'active'].includes(session.status) && <Link to={`/session/${session.id}`}>{copy.room}</Link>}
                      {session.cancelRequiresActionBy === 'teller' || (session.cancelRequested && session.cancelRequestedBy === 'client') ? (
                        <>
                          <button type="button" onClick={() => handleAction('approve-cancel', session)}>{t('workflow.teller.approveCancel')}</button>
                          <button type="button" onClick={() => handleAction('decline-cancel', session)}>{t('workflow.teller.declineCancel')}</button>
                        </>
                      ) : null}
                      {['paid', 'confirmed', 'active'].includes(session.status) && <button type="button" onClick={() => handleAction('complete', session)}>{t('workflow.teller.complete')}</button>}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </section>

        <section className="dashboard-card teller-calendar-card" id="availability-calendar">
          <div className="section-title-row">
            <h2>{t('workflow.teller.availability')}</h2>
            <span>{stats.openSlots} {copy.upcoming} · {slots.length} {copy.total}</span>
          </div>
          <div className="availability-calendar-layout">
            <div className="availability-calendar-shell">
              <Calendar
                value={selectedCalendarDate}
                onChange={(value) => {
                  if (value instanceof Date) {
                    setSelectedCalendarDate(value);
                  }
                }}
                tileClassName={({ date, view }) => (
                  view === 'month' && slotsByDate.has(localDateKey(date)) ? 'has-availability' : null
                )}
                tileContent={({ date, view }) => {
                  if (view !== 'month') return null;
                  const count = slotsByDate.get(localDateKey(date))?.length || 0;
                  return count > 0 ? <span className="calendar-slot-count">{count}</span> : null;
                }}
              />
              <div className="calendar-legend">
                <span><i className="available-dot" /> {copy.available}</span>
                <span><i className="reserved-dot" /> {copy.reserved}</span>
              </div>
            </div>
            <div className="availability-day-panel">
              <div className="availability-day-heading">
                <div>
                  <p className="eyebrow">{t('workflow.teller.selectedDay')}</p>
                  <h3>{new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(selectedCalendarDate)}</h3>
                </div>
                <span>{selectedDaySlots.length} {copy.slots}</span>
              </div>
              {selectedDaySlots.length === 0 ? (
                <div className="empty-card">
                  {slots.length === 0
                    ? t('workflow.teller.noAvailability')
                    : t('workflow.teller.noDay')}
                </div>
              ) : (
                <div className="availability-day-list">
                  {selectedDaySlots.map((slot) => (
                    <article className="slot-card" key={`${slot.source}-${slot.id}`}>
                      <div>
                        <strong>{translateSessionName(slot.sessionName, t, copy.readingFallback)}</strong>
                        <span>{formatDateTime(slot)}</span>
                        <small>{slot.duration} min · {translateSessionMode(slot.sessionType, t)}</small>
                      </div>
                      <div className="slot-card-meta">
                        <div className="slot-price">{formatPaymentAmount(slot.price, slot.currency)}</div>
                        <span className={`status-pill ${slot.status === 'available' ? 'online' : ''}`}>{translateSessionStatus(slot.status, t)}</span>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TellerDashboard;
